<template>
  <div>
    <el-table
      class="subproperty-list-table"
      :class="{ 'el-table--simple': simple }"
      ref="table"
      row-key="id"
      v-if="rows && rows.length > 0"
      v-loading="loading || destroyLoading"
      :data="rows"
      @sort-change="doChangeSort"
      @row-click="onRowClick"
    >
      <!-- Name -->
      <el-table-column
        :resizable="false"
        :label="!isPropertyNBA ? fields.name.label : i18n('entities.base.fields.name')"
        :prop="fields.name.name"
      >
        <template slot-scope="scope">
          <div
            class="list__avatar"
            :style="{ backgroundImage: getSubpropertyAvatar(scope.row) }"
          />
          <el-tooltip
            :disabled="!hasPermissionToAuditLogs"
            effect="dark"
            :content="scope.row.id"
            placement="bottom"
          >
            <span
              class="list__avatar__name"
              v-text="presenter(scope.row, 'name')"
            />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Country -->
      <el-table-column :resizable="false"  :label="fields.country.label" :prop="fields.country.name">
        <template slot-scope="scope">{{ presenter(scope.row, 'country') }}</template>
      </el-table-column>

      <!-- Managers -->
      <el-table-column :resizable="false" :label="fields.managers.label" :prop="fields.emails.name">
        <template slot-scope="scope">
          <app-i18n
            v-if="!scope.row.managers || !scope.row.managers.length"
            code="common.none"
          />
          <span
            v-else-if="scope.row.managers.length === 1"
            v-text="scope.row.managers[0].fullName || scope.row.managers[0].email"
          />
          <el-tooltip
            v-else
            effect="dark"
            :content="scope.row.managers.map(manager => manager.fullName || manager.email).join('\n')"
            placement="bottom"
          >
            <app-i18n code="entities.property.list.multipleManagers" />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Programs -->
      <el-table-column 
        :resizable="false" 
        :label="!isPropertyNBA ? fields.programs.label : i18n('entities.base.fields.seasons')" 
        :prop="fields.programs.name"
      >
        <template slot-scope="scope">
            <span
              v-for="(program, i) in getActiveProgramsByType(scope.row.programs)"
              :key="program.type"
            >
              {{program.amount}}
              <app-i18n v-if="isPropertyNBA" code="entities.season.list.singular" />
              <app-i18n v-else :code="`entities.program.enumerators.type.${ program.type }`" />
              <span v-text="`${i < (getActiveProgramsByType(scope.row.programs).length - 1) ? ', ' : ''}`" />
            </span>
            <app-i18n
              v-if="!getActiveProgramsByType(scope.row.programs).length"
              code="common.none"
            />
        </template>
      </el-table-column>

      <!-- Created Al -->
      <el-table-column
        v-if="hasPermissionToAuditLogs"
        :label="fields.createdAt.label"
        :prop="fields.createdAt.name"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'createdAt') }}</template>
      </el-table-column>

      <!-- Actions -->
      <el-table-column v-if="!simple" :resizable="false" :fixed="isMobile? undefined : 'right'" align="center" width="140">
        <template slot-scope="scope">
          <div class="table-actions">
            
            <router-link :to="`/subproperty/${scope.row.id}/edit`" v-if="hasPermissionToEdit">
              <el-button
                icon="icon-pen"
                circle
              />
            </router-link>

            <el-button
              :disabled="destroyLoading"
              @click="doDestroyWithConfirm(scope.row.id)"
              v-if="hasPermissionToDestroy"
              icon="icon-trash"
              circle
            />

          </div>
        </template>
      </el-table-column>
    </el-table>

    <app-empty-page
      v-else
      :icon-class="'icon-cup'"
      :empty-text="isPropertyNBA ? i18n('entities.base.empty') : i18n('entities.subproperty.empty')"
    />    

    <div class="el-pagination-wrapper" v-if="rows && rows.length > 0">
      <el-pagination
        v-if="!simple"
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { SubpropertyModel } from '@modules/subproperty/subproperty-model';
import { mapGetters, mapActions } from 'vuex';
import { SubpropertyPermissions } from '@modules/subproperty/subproperty-permissions';
import { i18n } from '@shared/i18n';
import manageType from '@shared/manage/manage-enum';
import  TagsFactory  from '@shared/factories/tags-factory';

const { fields } = SubpropertyModel;

export default {
  name: 'app-subproperty-list-table',

  props: {
    simple: Boolean,
    maxItems: Number
  },

  mounted() {
    this.doFetch();
  },

  computed: {
    ...mapGetters({
      allRows: 'subproperty/list/rows',
      count: 'subproperty/list/count',
      loading: 'subproperty/list/loading',
      pagination: 'subproperty/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      destroyLoading: 'subproperty/destroy/loading',
      paginationLayout: 'layout/paginationLayout',
      currentManage: 'auth/manage/currentManage',
      isPropertyNBA: 'auth/manage/isPropertyNBA',
    }),

    rows() {
      if (!this.maxItems) return this.allRows;
      return this.allRows.slice(0, this.maxItems);
    },

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new SubpropertyPermissions(this.currentUser)
        .edit;
    },

    hasPermissionToDestroy() {
      return new SubpropertyPermissions(this.currentUser)
        .destroy;
    },

    fields() {
      return fields;
    },

    getActiveProgramsByType() {
      // TODO: refactor
      // NOTE: right now the status field is not in the program
      return (programs) => {
        const programsByType = programs.reduce((result, { type }) => {
          //if (status !== 'enabled') return result;
          if (!type) return result;
          if (typeof result[type] !== 'object') {
            result[type] = {
              type,
              amount: 1
            };
          } else {
            result[type].amount++;
          }
          return result;
        }, {});

        return Object.values(programsByType) || [];
      }
    }
  },

  methods: {
    ...mapActions({
      doChangeSort: 'subproperty/list/doChangeSort',
      doChangePaginationCurrentPage:
        'subproperty/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize:
        'subproperty/list/doChangePaginationPageSize',
      doDestroy: 'subproperty/destroy/doDestroy',
      doChangeManage: 'auth/manage/doChangeManage',
      doFetch: 'subproperty/list/doFetch',
    }),

    presenter(row, fieldName) {
      return SubpropertyModel.presenter(row, fieldName);
    },

    async doDestroyWithConfirm(id) {
      try {
        await this.$confirm(
          i18n('common.areYouSure'),
          i18n('common.confirm'),
          {
            confirmButtonText: i18n('common.yes'),
            cancelButtonText: i18n('common.no'),
            type: 'warning',
          },
        );

        return this.doDestroy(id);
      } catch (error) {
        // no
      }
    },
    async doManage(model) {
      this.doChangeManage({
        manage: {
          ...this.currentManage,
          type: manageType.values.SUBPROPERTY,
          subproperty: model,
          subpropertyId: model.id,
          model,
          path: '/subproperty'
        },
        forceFetch: true,
      })
    },

    onRowClick(row, column) {
      if (!('label' in column)) return;
      this.doManage(row);
    },

    getSubpropertyAvatar(row) {
      if (!('logo' in row)) return null;
      if (!(row.logo instanceof Array)) return null;
      if (!row.logo.length) return null;
      return `url("${row.logo[0].publicUrl}")`;
    },
    getTagStyle({ keyEnum, valueEnum }) {
      return  new TagsFactory({entityName: 'common'}).getTagStyle({keyEnum: keyEnum, valueEnum: valueEnum});
    },
    i18n(code) {
      return i18n(code);
    },
  },
};
</script>
